import { useCallback, useRef, useState } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'

import classnames from 'classnames/bind'
import Layout from 'components/shared/Layout'
import LoadMore from 'components/shared/LoadMore'
import { Text } from 'components/typo'
import InputText from './FilterBar/InputText'

import MultiSelect from '../MultiSelect'
import OptionTag from '../MultiSelect/OptionTag'
import CardsGrid from '../CardsGrid'

import css from './styles.module.scss'
import algolia from 'src/services/algolia'
import QueryString from 'qs'
import PartnersSideBar from './PartnersSidebar'
import { slugify } from 'src/lib/slugify'

import { useRouter } from 'next/router'

const cx = classnames.bind(css)

/**
 * Component that renders a "No results" message.
 */
const NoResults = () => (
  <div className={css.noResults}>
    <Text>No results</Text>
  </div>
)

/**
 * Converts filters object to a string suitable for Algolia queries.
 * @param {Object} filters - The filters object to convert.
 * @returns {string} - The formatted string for Algolia queries.
 */
const parseFiltersToString = (filters) => {
  const filtersFormatted = []
  if (!filters) return filtersFormatted

  Object.keys(filters).forEach((key) => {
    const keyFilters = filters[key]

    if (!keyFilters || keyFilters.length === 0) {
      return
    }

    if (keyFilters.length === 1) {
      filtersFormatted.push(`${key}:"${keyFilters[0].label}"`)
    } else {
      const filters = keyFilters.map((filter) => `${key}:"${filter.label}"`)
      filtersFormatted.push(`(${filters.join(' OR ')})`)
    }
  })

  return filtersFormatted.join(' AND ')
}

/**
 * Converts a string from Algolia queries to a filters object.
 * @param {string} filters - The string to convert.
 * @returns {Object} - The filters object.
 */
const parseStringToFilters = (filters) => {
  if (!filters) return {}

  const filtersFormatted = QueryString.parse(filters, {
    ignoreQueryPrefix: true,
  })

  const stringFilters = filtersFormatted.filters

  if (!stringFilters) return {}

  const filtersArray = stringFilters.split(' AND ')

  const filtersObject = filtersArray.reduce((acc, filter) => {
    const [key, value] = filter.split(':')
    const values = value.split(' OR ')

    acc[key] = values.map((value) => ({ key, label: value.replace(/"/g, '') }))

    return acc
  }, {})

  return filtersObject
}

/**
 * Custom hook for handling articles data and interactions.
 * @param {string} index - The Algolia index.
 * @param {Object} initialAlgolia - Initial Algolia data.
 * @param {Function} adapter - Function to adapt Algolia hits.
 * @param {number} hitsPerPage - Number of hits per page.
 * @returns {Object} - The state and functions for article handling.
 */
const useArticles = (index, initialAlgolia, adapter, hitsPerPage = 6) => {
  const isFirstRender = useRef(true)
  const [algoliaData, setAlgoliaData] = useState(initialAlgolia)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState(
    parseStringToFilters(initialAlgolia?.params)
  )
  const parsedFilters = parseFiltersToString(filters)
  const currentHitsPerPage = hitsPerPage * (page + 1)
  const items = adapter ? adapter(algoliaData.hits) : algoliaData.hits

  useIsomorphicLayoutEffect(() => {
    /**
     * Fetches data from Algolia based on search, filters, and pagination.
     * @async
     */
    const fetchData = async () => {
      const data = await algolia(index).search(search, {
        page: 0,
        hitsPerPage: currentHitsPerPage,
        filters: parsedFilters,
      })

      setAlgoliaData(data)
    }

    if (!isFirstRender.current) {
      fetchData()
    }
  }, [currentHitsPerPage, search, parsedFilters])

  useIsomorphicLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
    }
  }, [])

  /**
   * Updates filters based on the provided key and array.
   * @param {string} key - The key for the filter.
   * @param {Array} array - The array of filter values.
   */
  const onFilter = (key, array) =>
    setFilters((prev) => ({ ...prev, [key]: array }))

  /**
   * Removes a filter based on the provided key and tag.
   * @param {string} key - The key for the filter.
   * @param {string} tag - The tag to remove.
   */
  const onRemoveFilter = useCallback(
    (key, tag) => {
      const currentFilter = filters[key]
      const tagIndex = currentFilter.findIndex((el) => el.label === tag)
      currentFilter.splice(tagIndex, 1)
      setFilters((prev) => ({ ...prev, [key]: currentFilter }))
    },
    [filters, setFilters]
  )

  return {
    items,
    setNextPage: () => setPage(page + 1),
    canFetchMore: algoliaData.hits.length < algoliaData.nbHits,
    onSearch: setSearch,
    onFilter,
    onRemoveFilter,
    filters,
  }
}

/**
 * Main component for displaying Algolia articles.
 * @param {Object} props - Component props.
 * @param {string} props.index - Algolia index.
 * @param {Object} props.initialAlgolia - Initial Algolia data.
 * @param {Function} props.adapter - Function to adapt Algolia hits.
 * @param {string} props.className - Additional CSS class for styling.
 * @param {React.Component} props.customCardComponent - Custom card component.
 * @param {Object} props.customCardProps - Props for custom card component.
 * @param {string} props.pathname - Current pathname.
 * @param {boolean} props.fullSearch - Flag for full search.
 * @param {boolean} props.hideSearchBar - Flag to hide search bar.
 * @param {string} props.labelType - Label for the type of articles.
 * @param {Array} props.facets - Array of filter facets.
 * @param {boolean} props.isAdvancedFilter - Flag for advanced filter.
 * @returns {React.Component} - Rendered AlgoliaArticles component.
 */
const AlgoliaArticles = ({
  index,
  initialAlgolia,
  adapter,
  className,
  customCardComponent,
  customCardProps,
  pathname,
  fullSearch,
  hideSearchBar,
  labelType,
  facets,
  isAdvancedFilter,
}) => {
  const {
    items,
    page,
    canFetchMore,
    setNextPage,
    onSearch,
    onFilter,
    filters,
    onRemoveFilter,
  } = useArticles(index, initialAlgolia, adapter, 12)

  const router = useRouter()
  const { asPath } = router
  return (
    <div className={cx(css.AlgoliaArticles, className)}>
      <Layout type="wrapper">
        <div className={isAdvancedFilter ? css.innerWrapper : null}>
          {isAdvancedFilter ? (
            <PartnersSideBar
              fullSearch={fullSearch}
              facets={facets}
              facetFilters={filters}
              onSearchChange={onSearch}
              setFilter={(key, newValue) => {
                const currentFacet = filters[key]
                const newFilter = !currentFacet
                  ? [{ label: newValue }]
                  : currentFacet.some((item) => item.label === newValue)
                  ? currentFacet.filter((item) => item.label !== newValue)
                  : [...currentFacet, { label: newValue }]

                onFilter(key, newFilter)
              }}
            />
          ) : (
            <>
              <div className={css.filterBar}>
                <div className={css.multiSelectsWrapper}>
                  {facets.map((facet, id) =>
                    facet.multi ? (
                      <MultiSelect
                        className={css.multiSelect}
                        onChange={onFilter}
                        name={facet.label}
                        facetKey={facet.key}
                        options={facet.options.map((option) => ({
                          label: option,
                        }))}
                        key={id}
                        checkbox
                      />
                    ) : (
                      <MultiSelect
                        className={css.multiSelect}
                        onChange={onFilter}
                        name={facet.label}
                        facetKey={facet.key}
                        options={facet.options.map((option) => ({
                          label: option,
                        }))}
                        key={id}
                      />
                    )
                  )}
                </div>
                {!hideSearchBar && (
                  <InputText
                    className={cx(css.search)}
                    aria-label="search blog post"
                    onChange={onSearch}
                    placeholder="Search"
                    icon="search"
                  />
                )}
              </div>

              <div className={css.tagsResults}>
                <div className={css.tagsWrapper}>
                  {Object.keys(filters).map(
                    (key) =>
                      !!filters[key] &&
                      filters[key].map((tag, index) => {
                        if (!key === 'publishedAt') {
                          return (
                            <OptionTag
                              key={index}
                              onClick={onRemoveFilter}
                              facetKey={key}
                              text={tag.label}
                            />
                          )
                        }
                      })
                  )}
                </div>
                <Text className={css.results} size="small">
                  {`Showing ${items.length} of ${initialAlgolia.nbHits}`}
                  <span> {labelType}</span>
                </Text>
              </div>
            </>
          )}
          <div className={css.partnersContent}>
            {items.length > 0 ? (
              <CardsGrid
                cards={items.map((item) => {
                  return asPath === '/showcases'
                    ? {
                        ...item,
                        link: {
                          ...item.link,
                          href: `/showcases/${slugify(item.slug)}`,
                        },
                      }
                    : item
                })}
                resize
                customCardComponent={customCardComponent}
                customCardProps={customCardProps}
              />
            ) : (
              <NoResults />
            )}

            {canFetchMore && (
              <LoadMore page={page} url={pathname} onClick={setNextPage} />
            )}
          </div>
        </div>
      </Layout>
    </div>
  )
}

export default AlgoliaArticles
